import { requestGQL } from 'common/graphQlClient';
import { isTestEnv, oneHourInMs } from 'config';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { G, O, pipe } from '@mobily/ts-belt';

import { GET_PROPERTY_CONNECTION, GetPropertyConnectionResponse } from '../api';
import { getCrossConnectionPanels, getODFs, parseConnection, getTPOutlets } from '../Connection';
import { type Outlet } from '../../outlets/Outlet';
import type { TPOutlet } from '../../outlets/TPOutlet';
import { optionParseInt } from '../../../common/helpers';

export const useConnection = (propertyId: undefined | null | number) => {
  const { data, isLoading, isError, isRefetching } = useQuery<GetPropertyConnectionResponse>(
    ['propertyConnection', propertyId],
    () => requestGQL(GET_PROPERTY_CONNECTION, { id: propertyId }),
    {
      enabled: G.isNotNullable(propertyId),
      refetchOnWindowFocus: false,
      retry: isTestEnv ? 0 : 3,
      staleTime: oneHourInMs,
    },
  );

  const parsedData = useMemo(() => (G.isNotNullable(data) ? parseConnection(data) : data), [data]);

  const serialized2 = useMemo(() => parsedData?.serialized2, [parsedData]);

  const ODFnodes = useMemo(() => pipe(O.fromNullable(serialized2), O.map(getODFs), O.toNullable), [serialized2]);
  const areaTPOutlets = useMemo(
    () => pipe(O.fromNullable(serialized2), O.map(getTPOutlets), O.toNullable),
    [serialized2],
  );

  const crossConnectionPanelNodes = useMemo(
    () => pipe(O.fromNullable(serialized2), O.map(getCrossConnectionPanels), O.toNullable),
    [serialized2],
  );

  const outlets: Outlet[] = useMemo(() => {
    return (
      serialized2?.nodeDataArray
        .filter(({ model }) => model === 'outlet')
        ?.map((node) => ({
          id: node.id,
          location: node.cfObject?.location ?? null,
          contactType: node.cfObject?.contactType ?? '-',
          imagesUrls: node.cfObject?.images ?? [],
          premiseId: node.cfObject?.premisesId ?? optionParseInt(node.group) ?? 0,
          fibers: serialized2?.linkDataArray
            .filter(({ to }) => to === node.id)
            .map((link) => {
              const linkedODF = ODFnodes?.find(({ id }) => link.from === id);
              return {
                id: link.id,
                toPort: link.toPort ?? null,
                odf: linkedODF?.text ?? null,
                odfId: linkedODF?.id ?? null,
                fiber: link.fromPort ?? null,
                duct: link.cfObject?.duct ?? null,
                length: link.cfObject?.length ?? null,
                db1310: link.cfObject?.db1310 ?? null,
                db1550: link.cfObject?.db1550 ?? null,
                contract: link.cfObject?.designator ?? null,
                personId: link.cfObject?.personId ?? null,
              };
            }),
        })) ?? []
    );
  }, [serialized2]);

  const premiseTPOutlets: TPOutlet[] = useMemo(
    () =>
      (
        serialized2?.nodeDataArray.filter(
          ({ model, group }) => model === 'tpOutlet' && group !== undefined && group !== null,
        ) ?? []
      ).map((node) => ({
        id: node.id,
        text: node.text || node.cfObject?.text || '',
        location: node.cfObject?.location ?? null,
        imagesUrls: node.cfObject?.images ?? [],
        premiseId: node.cfObject?.premisesId ?? optionParseInt(node.group) ?? 0,
        fibers:
          serialized2?.linkDataArray
            .filter(({ to }) => to === node.id)
            .map((link) => {
              const linkedCrossPanel = crossConnectionPanelNodes?.find(({ id }) => link.from === id);
              return {
                id: link.id,
                category: link.cfObject?.category ?? '',
                from: {
                  nodeId: link.from,
                  portId: link.fromPort,
                },
                to: {
                  nodeId: link.to,
                  portId: link.toPort,
                },
                length: link.cfObject?.length ?? 0,
                personId: link.cfObject?.personId ?? null,
                shielding: link.cfObject?.shielding ?? '',
                crossConnectionPanelName: link.cfObject?.crossConnectionPanel ?? linkedCrossPanel?.text ?? '',
              };
            }) ?? [],
      })),
    [serialized2],
  );

  const id = useMemo(() => parsedData?.id, [parsedData]);

  return {
    data: parsedData,
    id,
    isLoading,
    isError,
    ODFnodes,
    crossConnectionPanelNodes,
    areaTPOutlets,
    premiseTPOutlets,
    serialized2,
    outlets,
    isRefetching,
  };
};
